import { Button as MuiButton, styled, CSSObject, alpha } from "@mui/material";
import {
  brandColors,
  resolveColorToken,
} from "../../../../stories/helpers/ColorTokenHelper";
import { ColorTokensEnum } from "../../../../enums/tokens/colorTokensEnum";
import { TextButtonProps } from "@/app/interfaces/props/TextButtonProps";
import { ElementSizeTokensEnum } from "../../../../enums/tokens/elementSizeTokensEnum";
import { StateTokensEnum } from "../../../../enums/tokens/stateTokensEnum";
import typographyTokens from "../../../../stories/tokens/typography-tokens.json";
import { TypographyTokensEnum } from "../../../../enums/tokens/typographyTokensEnum";
import { radii } from "../../../../stories/helpers/RadiusTokenHelper";
import { RadiusTokensEnum } from "../../../../enums/tokens/radiusTokensEnum";

const sizeTypographyMap = {
  [ElementSizeTokensEnum.SMALL]: TypographyTokensEnum.BODY_2_MEDIUM,
  [ElementSizeTokensEnum.DEFAULT]: TypographyTokensEnum.BODY_1_MEDIUM,
  [ElementSizeTokensEnum.LARGE]: TypographyTokensEnum.HEADING_4,
  [ElementSizeTokensEnum.XLARGE]: TypographyTokensEnum.HEADING_3,
} as const;

const DEFAULT_ICON_SIZE = 20;

const StyledTextButton = styled(MuiButton, {
  shouldForwardProp: (prop) => !["ownerState"].includes(prop as string),
})<{ ownerState: TextButtonProps }>(({ ownerState }): CSSObject => {
  const currentSize = ownerState.size || ElementSizeTokensEnum.DEFAULT;
  const typographyKey = sizeTypographyMap[
    currentSize
  ] as keyof typeof typographyTokens;
  const typography = (typographyTokens[typographyKey] || {}) as CSSObject;

  const resolvedColor = resolveColorToken(
    ownerState.color,
    brandColors[ColorTokensEnum.DARKEST_GREY]
  );

  const resolvedIconSize =
    typeof ownerState.iconSize === "number"
      ? `${ownerState.iconSize}px`
      : ownerState.iconSize;

  return {
    ...typography,
    textTransform: "none",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "6px",
    borderRadius: radii[RadiusTokensEnum.SM],
    backgroundColor: brandColors[ColorTokensEnum.WHITE],
    color: resolvedColor,
    border: "1px solid transparent",
    transition: "all 0.2s ease-in-out",

    ...(resolvedIconSize && {
      "& .MuiButton-startIcon, & .MuiButton-endIcon": {
        color: resolvedColor,
        "& > *:first-of-type": { fontSize: resolvedIconSize },
        "& svg, & img": {
          width: resolvedIconSize,
          height: resolvedIconSize,
          fontSize: resolvedIconSize,
        },
      },
    }),

    // --- DIMENSIONS ---
    ...(ownerState.size === ElementSizeTokensEnum.XLARGE && {
      height: "24px",
      padding: "0 8px",
      minWidth: "67px",
    }),

    ...(ownerState.size === ElementSizeTokensEnum.LARGE && {
      height: "24px",
      padding: "0 6px",
      minWidth: "56px",
    }),

    ...(ownerState.size === ElementSizeTokensEnum.DEFAULT && {
      height: "24px",
      padding: "0 4px",
      minWidth: "45px",
    }),

    ...(ownerState.size === ElementSizeTokensEnum.SMALL && {
      height: "24px",
      padding: "0 2px",
      minWidth: "45px",
    }),

    // --- INTERACTION STATES ---
    "&:hover": {
      backgroundColor: brandColors[ColorTokensEnum.HOVER_LIGHT],
      color: resolvedColor,
    },

    "&:focus-visible": {
      outline: "none",
      backgroundColor: brandColors[ColorTokensEnum.WHITE],
      color: resolvedColor,
      border: `1px solid ${brandColors[ColorTokensEnum.HOVER]}`,
      boxShadow: `0 0 0 4px ${alpha(brandColors[ColorTokensEnum.HOVER], 0.25)}`,
    },

    // --- MANUAL STATE OVERRIDES (STORYBOOK) ---
    ...(ownerState.state === StateTokensEnum.HOVER && {
      backgroundColor: brandColors[ColorTokensEnum.HOVER_LIGHT],
      color: resolvedColor,
      "&:focus": {
        border: "none",
      },
    }),

    ...(ownerState.state === StateTokensEnum.FOCUS && {
      border: `1px solid ${brandColors[ColorTokensEnum.HOVER]}`,
      boxShadow: `0 0 0 4px ${alpha(brandColors[ColorTokensEnum.HOVER], 0.25)}`,
      color: resolvedColor,
      "&:hover": {
        backgroundColor: brandColors[ColorTokensEnum.HOVER_LIGHT],
      },
    }),

    // --- DISABLED ---
    "&:disabled": {
      backgroundColor: brandColors[ColorTokensEnum.DISABLED],
      color: brandColors[ColorTokensEnum.WHITE],
      border: "none",
      pointerEvents: "auto",
      "&:hover": {
        cursor: `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' viewBox='0 0 24 24' fill='%23D50000'><path d='M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm0 18c-4.42 0-8-3.58-8-8 0-1.85.63-3.55 1.69-4.9L16.9 18.31C15.55 19.37 13.85 20 12 20zm5.31-3.1L6.69 5.69A7.966 7.966 0 0112 4c4.42 0 8 3.58 8 8 0 1.85-.63 3.55-1.69 4.9z'/></svg>") 12 12, not-allowed`,
      },
    },
  };
});

export const TextButton = ({
  label,
  size = ElementSizeTokensEnum.DEFAULT,
  state = StateTokensEnum.DEFAULT,
  color,
  iconSize = DEFAULT_ICON_SIZE,
  leftIcon: LeftIcon,
  rightIcon: RightIcon,
  ...props
}: TextButtonProps) => {
  return (
    <StyledTextButton
      ownerState={{ label, size, state, color, iconSize }}
      disabled={state === StateTokensEnum.DISABLED}
      disableRipple
      startIcon={LeftIcon ? <LeftIcon /> : undefined}
      endIcon={RightIcon ? <RightIcon /> : undefined}
      {...props}
    >
      {label}
    </StyledTextButton>
  );
};
